// Builds the ordercapture/services/createOrder payload from ProposalTool data.
// Mirrors the captureOrder5NewUpgrade('send-link') logic in new-order.js but
// sources all fields from proposalInfo + recurlyAccountInfo instead of
// the legacy orderData5CU shape.

// Plans where Recurly encodes the dollar budget as the subscription quantity
// (unitAmount = 1, quantity = $amount). Their per-unit price is always 1 and
// must NOT be divided by quantity. Mirrors the same set in useProposalSave.js.
const QUANTITY_AS_DOLLAR_CODES = new Set([
  'tl-tax-5-thryv-m2m',
  'tl-mc-5-thryv-m2m',
])

function buildLines(selectedOption) {
  const lines = []
  let lineCounter = 1

  const items = (selectedOption?.items ?? []).filter(
    item => item?.productCode && !item.alreadyIncluded,
  )

  for (const item of items) {
    const isCenter = item.productType === 'CENTER'
    const isOneTimeFee = item.productType === 'ONE_TIME_FEE'
    const parentLineNumber = lineCounter
    const perUnitPrice =
      item.unitPrice != null &&
      (item.quantity ?? 1) > 1 &&
      !QUANTITY_AS_DOLLAR_CODES.has(item.productCode)
        ? item.unitPrice / item.quantity
        : item.unitPrice ?? null

    lines.push({
      line_number: lineCounter++,
      parent_line_number: null,
      plansv5_id: null,
      action_code: 'New',
      order_line_status: 'Completed',
      saas_product_category: isCenter ? 'Center' : 'App',
      saas_product_type: isOneTimeFee ? 'one_time_fee' : 'main',
      saas_product_code: item.productCode,
      saas_instance_id: null,
      recurly_subscription_id: null,
      total_cost: item.unitPrice ?? null,
      unit_cost: perUnitPrice,
      quantity: item.quantity ?? 1,
      start_date: new Date(),
      end_date: new Date(),
    })

    const addons = (item.addons ?? []).filter(
      addon => addon?.productCode && !addon.alreadyIncluded,
    )
    for (const addon of addons) {
      const addonPerUnit =
        addon.unitPrice != null &&
        (addon.quantity ?? 1) > 1 &&
        !QUANTITY_AS_DOLLAR_CODES.has(addon.productCode)
          ? addon.unitPrice / addon.quantity
          : addon.unitPrice ?? null

      lines.push({
        line_number: lineCounter++,
        parent_line_number: parentLineNumber,
        plansv5_id: null,
        action_code: 'New',
        order_line_status: 'Completed',
        saas_product_category: isCenter ? 'Center' : 'App',
        saas_product_type: isCenter ? 'entitlement' : 'addon',
        saas_product_code: addon.productCode,
        saas_instance_id: null,
        recurly_subscription_id: null,
        total_cost: addon.unitPrice ?? null,
        unit_cost: addonPerUnit,
        quantity: addon.quantity ?? 1,
        start_date: new Date(),
        end_date: new Date(),
      })
    }
  }

  return lines
}

function buildDiscounts(coupons) {
  return (coupons ?? [])
    .filter(c => c.isValid)
    .map((coupon, index) => ({
      line_number: index + 1,
      discount_type: coupon.type ?? null,
      coupon_code: coupon.code ?? coupon.couponCode,
      discount_percentage: coupon.discountPercent ?? null,
      discount_amount: coupon.discountAmount ?? null,
      number_of_months: coupon.durationAmount ?? null,
      start_date: new Date(),
      end_date: null,
      created_date: null,
      create_oprid: null,
      updated_date: null,
      update_oprid: null,
    }))
}

export function buildCaptureOrderPayload(
  proposalInfo,
  recurlyAccountInfo,
  selectedOption,
  accountThryvId = '',
  paymentUuid = '',
) {
  const addr = recurlyAccountInfo?.address ?? {}
  const firstName =
    recurlyAccountInfo?.firstName || proposalInfo.clientFirstName || ''
  const lastName =
    recurlyAccountInfo?.lastName || proposalInfo.clientLastName || ''

  return {
    order_status: 'Pending',
    order_version: '5.0',
    order_flow: 'New',
    order_submitted_by: 'system',
    order_completed_date: null,

    industry_id: null,
    industry_description: null,
    country_code: proposalInfo.country || 'US',

    account_email: proposalInfo.clientEmail || recurlyAccountInfo?.email || '',
    contact_first_name: firstName,
    contact_last_name: lastName,
    contact_phone: proposalInfo.clientPhone || addr.phone || '',
    contact_mobile: proposalInfo.clientPhone || addr.phone || '',
    contact_email: proposalInfo.clientEmail || recurlyAccountInfo?.email || '',

    company_name: proposalInfo.businessName || '',
    company_address1: addr.street1 || addr.street || '',
    company_address2: addr.street2 || null,
    company_city: addr.city || '',
    company_state: addr.region || addr.state || '',
    company_zipcode: addr.zip || addr.postalCode || '',
    company_phone: addr.phone || '',

    sales_rep_id: proposalInfo.salesRepId || null,
    on_behalf_of: proposalInfo.behalfEmail || null,
    xp_code: proposalInfo.xpCode || null,
    abn: proposalInfo.abn || null,

    thryv_id: proposalInfo.thryvId || accountThryvId || '',
    account_code: recurlyAccountInfo?.code || null,
    eaid: 'unknown',

    is_invoice_billed: 0,
    is_cohort: null,
    demo_lead_id: null,
    no_service: null,

    onboarding_appt_id: proposalInfo.apptId || null,
    creative_appt_id: null,
    thryv_success_services_appt_id: null,
    update_to_autopay: 1,
    on_boarding_fee: 0,

    purchase_intent_1: null,
    purchase_intent_2: null,
    purchase_intent_3: null,
    sensis_site: null,

    keap_account: false,

    lines: buildLines(selectedOption),
    discounts: buildDiscounts(selectedOption?.coupons),
    reseller: [],
    payment_uuid: paymentUuid || null,
  }
}
